import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import { useState } from "react";
import type {
  CompanySource,
  EmailSource,
  StepRecord,
} from "../store/slices/chatSlice";
import MessageSteps from "../components/chat/MessageSteps";
import MessageRating from "../components/MessageRating";

const EMAIL_PREVIEW_COUNT = 5;

const sectionSx = {
  mb: 1,
  boxShadow: "none",
  border: "1px solid rgba(0, 0, 0, 0.12)",
  "&:before": { display: "none" },
  borderRadius: "8px !important",
} as const;

const summarySx = {
  minHeight: 36,
  "& .MuiAccordionSummary-content": { my: 0.5 },
  backgroundColor: "rgba(0, 0, 0, 0.02)",
  borderRadius: "8px",
} as const;

export function EmailsPart({ data }: { data: EmailSource[] }) {
  if (!data?.length) return null;
  return (
    <Accordion sx={sectionSx} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="caption" sx={{ fontWeight: 500, color: "text.secondary" }}>
            EMAIL SOURCES ({data.length} found)
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 1, pb: 1 }}>
        <Stack divider={<Divider flexItem />} spacing={1}>
          {data.slice(0, EMAIL_PREVIEW_COUNT).map((email, i) => (
            <Box key={`${email.subject}-${i}`} sx={{ py: 0.5 }}>
              <Typography sx={{ fontWeight: 500, fontSize: "0.8rem" }}>{email.subject}</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}>
                From: {email.from}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}>
                Date: {email.date}
              </Typography>
              {typeof email.score === "number" && (
                <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.65rem" }}>
                  Relevance: {(email.score * 100).toFixed(1)}%
                </Typography>
              )}
              {email.body && (
                <Accordion sx={{ mt: 1, boxShadow: "none", border: "1px solid rgba(0,0,0,0.08)", "&:before": { display: "none" }, borderRadius: "4px !important" }} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 28, "& .MuiAccordionSummary-content": { my: 0.25 } }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                      View email content
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", fontSize: "0.8rem", maxHeight: 400, overflow: "auto" }}>
                      {email.body}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          ))}
          {data.length > EMAIL_PREVIEW_COUNT && (
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
              ... +{data.length - EMAIL_PREVIEW_COUNT} more
            </Typography>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export function CompaniesPart({ data }: { data: CompanySource[] }) {
  if (!data?.length) return null;
  return (
    <Accordion sx={sectionSx} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <BusinessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="caption" sx={{ fontWeight: 500, color: "text.secondary" }}>
            COMPANIES ({data.length})
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 1, pb: 1 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {data.map((c, i) => {
            const label = c.name ?? c.title ?? c.domain ?? "(company)";
            const href = c.url;
            return href ? (
              <Chip
                key={`${label}-${i}`}
                label={label}
                size="small"
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                clickable
                sx={{ fontSize: "0.75rem" }}
              />
            ) : (
              <Chip key={`${label}-${i}`} label={label} size="small" sx={{ fontSize: "0.75rem" }} />
            );
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

// Wraps the shipped MessageRating component with the local visibility
// state it expects (in legacy Chatbot.tsx that state lived in a parent
// map; here each rating part owns its own state).
function RatingPart({ data }: { data: unknown }) {
  const { messageId } = (data as { messageId?: string }) ?? {};
  const [visible, setVisible] = useState(false);
  if (!messageId) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <MessageRating
        messageId={messageId}
        visible={visible}
        onToggleVisibility={() => setVisible((v) => !v)}
      />
    </Box>
  );
}

// Routes an assistant-ui DataMessagePart (the `aui-*` parts emitted by
// messageMapper) to its renderer. thread.tsx delegates here so the chat-v2
// data-part contract lives in one place — and stays unit-testable without
// standing up the whole <Thread/> runtime.
export function DataPart({ name, data }: { name: string; data: unknown }) {
  if (name === "aui-steps") return <MessageSteps steps={data as StepRecord[]} />;
  if (name === "aui-emails") return <EmailsPart data={data as EmailSource[]} />;
  if (name === "aui-companies")
    return <CompaniesPart data={data as CompanySource[]} />;
  if (name === "aui-rating") return <RatingPart data={data} />;
  return null;
}
